import "@/App.css";
import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { WalletProvider } from "@/context/WalletContext";
import { AuthProvider } from "@/context/AuthContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { Toaster } from "@/components/ui/sonner";
import { setShareMeta, backendOrigin } from "@/lib/meta";
import { isEcosystemPagePath, ecosystemRobotsFromWindow } from "@/lib/ecosystemSeo";
import LeaderboardPage from "@/pages/LeaderboardPage";
import SeasonPage from "@/pages/SeasonPage";
import BattlesPage from "@/pages/BattlesPage";
import BattlePage from "@/pages/BattlePage";
import CreateBattlePage from "@/pages/CreateBattlePage";
import NotificationsPage from "@/pages/NotificationsPage";
import PartnerCampaignPage from "@/pages/PartnerCampaignPage";
import RewardsPage from "@/pages/RewardsPage";
import ProjectWarRoomPage from "@/pages/ProjectWarRoomPage";
import EntityDetail from "@/pages/EntityDetail";
import Backers from "@/pages/Backers";
import StatsPage from "@/pages/StatsPage";
import RulesPage from "@/pages/RulesPage";
import AboutPage from "@/pages/AboutPage";
import UserProfile from "@/pages/UserProfile";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import RobinhoodEcosystemGate from "@/pages/RobinhoodEcosystemGate";
import SolanaEcosystemGate from "@/pages/SolanaEcosystemGate";
import BaseEcosystemGate from "@/pages/BaseEcosystemGate";
import Footer from "@/components/Footer";
import { useConfig } from "@/context/ConfigContext";
import { isRobinhoodEcosystemEnabled } from "@/lib/robinhoodFlags";
import { isSolanaEcosystemEnabled } from "@/lib/solanaFlags";
import { isBaseEcosystemEnabled } from "@/lib/baseFlags";

const FlipUtilityPage = lazy(() => import("@/pages/flip/FlipUtilityPage"));
const CallsPage = lazy(() => import("@/pages/flip/CallsPage"));
const CallDetailPage = lazy(() => import("@/pages/flip/CallDetailPage"));
const CampaignsPage = lazy(() => import("@/pages/flip/CampaignsPage"));
const CampaignDetailPage = lazy(() => import("@/pages/flip/CampaignDetailPage"));
const CommunityPage = lazy(() => import("@/pages/flip/CommunityPage"));
const BackerProfilePage = lazy(() => import("@/pages/flip/BackerProfilePage"));
const LaunchPage = lazy(() => import("@/pages/LaunchPage"));
const LaunchCreatePage = lazy(() => import("@/pages/LaunchCreatePage"));
const LaunchProjectPage = lazy(() => import("@/pages/LaunchProjectPage"));
const DiscoverPage = lazy(() => import("@/pages/DiscoverPage"));
const ChainsPage = lazy(() => import("@/pages/ChainsPage"));
const ChainDetailPage = lazy(() => import("@/pages/ChainDetailPage"));
const CallerIdentityPage = lazy(() => import("@/pages/CallerIdentityPage"));

function FlipRouteFallback() {
  return <div className="min-h-[40vh] bg-black" aria-hidden />;
}

// Forward navigation (click a token, open a page) starts at the top.
// Back/forward browser buttons keep the browser's own scroll restoration.
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useLayoutEffect(() => {
    if (navType !== "POP") window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

const STATIC_PAGE_META = {
  "/": {
    title: "Crypto Token Leaderboard — Back Your Bag | FLIP",
    description: "The people-powered crypto leaderboard. Every dollar verified on-chain.",
  },
  "/callers": {
    title: "Top Crypto Callers Leaderboard — FLIP",
    description: "Rank the most-backed callers on FLIP. Every contribution is verified on-chain and updates the board in real time.",
  },
  "/backers": {
    title: "Top Crypto Backers by On-Chain Support — FLIP",
    description: "The wallets moving the FLIP board. See who is backing tokens and callers, and how much they have staked.",
  },
  "/stats": {
    title: "Live Crypto Leaderboard Stats — FLIP",
    description: "Live FLIP platform statistics: total backed, contributions, flips and board activity — every dollar verified on-chain.",
  },
  "/rules": {
    title: "How FLIP Works — Rules",
    description: "How the FLIP leaderboard works: backing, ranks, flips and payouts, explained step by step.",
  },
  "/about": {
    title: "About FLIP — How the People-Powered Crypto Leaderboard Works",
    description: "FLIP is the people-powered crypto leaderboard: back your bag, take the top. Every dollar verified on-chain.",
  },
  "/ecosystem/robinhood": {
    title: "Robinhood Chain Ecosystem — BackOnFlip",
    description: "Discover Robinhood Chain assets, verified Backing ranks, callers, and Hype Radar on BackOnFlip. Market data is informational — rank is Backing only.",
  },
  "/chain/robinhood": {
    title: "Robinhood Chain Ecosystem — BackOnFlip",
    description: "Discover Robinhood Chain assets, verified Backing ranks, callers, and Hype Radar on BackOnFlip. Market data is informational — rank is Backing only.",
  },
  "/ecosystem/base": {
    title: "Base Ecosystem — BackOnFlip",
    description: "Discover Base assets, verified Backing ranks, callers, and Hype Radar on BackOnFlip. Market data is informational — rank is Backing only.",
  },
  "/chain/base": {
    title: "Base Ecosystem — BackOnFlip",
    description: "Discover Base assets, verified Backing ranks, callers, and Hype Radar on BackOnFlip. Market data is informational — rank is Backing only.",
  },
  "/ecosystem/solana": {
    title: "Solana Ecosystem — BackOnFlip",
    description: "Discover Solana assets, verified Backing ranks, callers, and Hype Radar on BackOnFlip. Market data is informational — rank is Backing only. Identity uses mint addresses on Solscan.",
  },
  "/chain/solana": {
    title: "Solana Ecosystem — BackOnFlip",
    description: "Discover Solana assets, verified Backing ranks, callers, and Hype Radar on BackOnFlip. Market data is informational — rank is Backing only. Identity uses mint addresses on Solscan.",
  },
  "/flip": {
    title: "$FLIP Utility Layer — BackOnFlip",
    description: "Holder tiers, reputation, Conviction Calls and campaigns around the verified backing leaderboard.",
  },
  "/calls": {
    title: "Conviction Calls — FLIP",
    description: "Reputation-only public calls on measurable BackOnFlip outcomes.",
  },
  "/campaigns": {
    title: "Community Campaigns — FLIP",
    description: "Community goals on BackOnFlip. Participation uses verified backing.",
  },
  "/launch": {
    title: "Multichain Launch — BackOnFlip",
    description: "Staging-safe multichain project discovery and launch preparation. Project identity is chain-independent; real broadcast is disabled in V1.",
  },
  "/discover": {
    title: "Discover Feed — BackOnFlip",
    description: "Social conviction graph feed: follows, theses, calls, and launch preparation events. Sponsored placements stay labeled.",
  },
  "/chains": {
    title: "Chain Radar — BackOnFlip",
    description: "Discover and score blockchain readiness. Chain Radar never auto-enables production networks.",
  },
};

function publicShareOrigin() {
  // Staging/local must not emit production canonical/og URLs.
  if (typeof window === "undefined" || !window.location?.hostname) {
    return "https://backonflip.com";
  }
  const host = window.location.hostname.toLowerCase();
  if (host === "backonflip.com" || host === "www.backonflip.com") {
    return "https://backonflip.com";
  }
  return window.location.origin.replace(/\/$/, "");
}

function RouteMeta() {
  const { pathname } = useLocation();
  const config = useConfig();
  const rhEnabled = isRobinhoodEcosystemEnabled(config);
  const solEnabled = isSolanaEcosystemEnabled(config);
  const baseEnabled = isBaseEcosystemEnabled(config);

  useEffect(() => {
    const isRh = pathname.startsWith("/ecosystem/robinhood") || pathname.startsWith("/chain/robinhood");
    const isSol = pathname.startsWith("/ecosystem/solana") || pathname.startsWith("/chain/solana");
    const isBase = pathname.startsWith("/ecosystem/base") || pathname.startsWith("/chain/base");
    if (isRh && !rhEnabled) return;
    if (isSol && !solEnabled) return;
    if (isBase && !baseEnabled) return;
    const page = STATIC_PAGE_META[pathname];
    if (!page) return;

    const backend = backendOrigin();
    const shareOrigin = publicShareOrigin();
    const boardImage =
      pathname === "/callers"
        ? `${backend}/api/og/board.png?category=callers&v=3`
        : `${backend}/api/og/board.png?v=3`;
    setShareMeta({
      ...page,
      image: boardImage,
      url: `${shareOrigin}${pathname}`,
      robots: isEcosystemPagePath(pathname)
        ? ecosystemRobotsFromWindow({ productionPublic: true })
        : "index,follow",
    });
  }, [pathname, rhEnabled, solEnabled, baseEnabled]);

  return null;
}

function App() {
  return (
    <div className="App">
      <ConfigProvider>
        <WalletProvider>
          <BrowserRouter>
              <ScrollToTop />
              <RouteMeta />
              <AuthProvider>
                <Suspense fallback={<FlipRouteFallback />}>
                  <Routes>
                    <Route path="/" element={<LeaderboardPage category="tokens" />} />
                    <Route path="/callers" element={<LeaderboardPage category="callers" />} />
                    <Route path="/token/:slug" element={<EntityDetail category="tokens" />} />
                    <Route path="/caller/:slug" element={<EntityDetail category="callers" />} />
                    <Route path="/backers" element={<Backers />} />
                    <Route path="/stats" element={<StatsPage />} />
                    <Route path="/season/:slug" element={<SeasonPage />} />
                    <Route path="/battles" element={<BattlesPage />} />
                    <Route path="/battle/:slug" element={<BattlePage />} />
                    <Route path="/battles/create" element={<CreateBattlePage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/rewards" element={<RewardsPage />} />
                    <Route path="/partner/campaigns/:slug" element={<PartnerCampaignPage />} />
                    <Route path="/project/:slug/war-room" element={<ProjectWarRoomPage />} />
                    <Route path="/rules" element={<RulesPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/ecosystem/robinhood" element={<RobinhoodEcosystemGate />} />
                    <Route path="/chain/robinhood" element={<RobinhoodEcosystemGate />} />
                    <Route path="/ecosystem/solana" element={<SolanaEcosystemGate />} />
                    <Route path="/chain/solana" element={<SolanaEcosystemGate />} />
                    <Route path="/ecosystem/base" element={<BaseEcosystemGate />} />
                    <Route path="/chain/base" element={<BaseEcosystemGate />} />
                    <Route path="/user/:wallet" element={<UserProfile />} />
                    <Route path="/flip" element={<FlipUtilityPage />} />
                    <Route path="/calls" element={<CallsPage />} />
                    <Route path="/calls/:id" element={<CallDetailPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/campaign/:id" element={<CampaignDetailPage />} />
                    <Route path="/community/:slug" element={<CommunityPage />} />
                    <Route path="/backer/:chain/:wallet" element={<BackerProfilePage />} />
                    <Route path="/launch" element={<LaunchPage />} />
                    <Route path="/launch/create" element={<LaunchCreatePage />} />
                    <Route path="/launch/project/:slug" element={<LaunchProjectPage />} />
                    <Route path="/discover" element={<DiscoverPage />} />
                    <Route path="/chains" element={<ChainsPage />} />
                    <Route path="/chains/:chain" element={<ChainDetailPage />} />
                    {/* Social identity — NOT /caller/:slug (EntityDetail KOLs board) */}
                    <Route path="/identity/:identity" element={<CallerIdentityPage />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                  </Routes>
                </Suspense>
              </AuthProvider>
              <Footer />
          </BrowserRouter>
          <Toaster position="top-center" theme="dark" />
        </WalletProvider>
      </ConfigProvider>
    </div>
  );
}

export default App;
